import {
    Address,
    EmployerSponsoredCoverage,
    HouseholdMembers,
    HouseholdMember,
    HraDetails,
    PersonName,
    Phone
} from '@components/ssap/SsapTypes';
import { pick } from './objectHelpers';

interface PartToSyncEmployerCoverage {
    address: Address,
    ein: string | null,
    name: PersonName,
    phone: Phone
}

interface PartToSyncHra extends Partial<HraDetails> {}

interface Identifiers {
    applicantGuid: string | null,
    employerName?: string | null,
    companyName?: string | null
}

class HouseholdMembersSynchronizer {
    _initialHouseholdMembers: HouseholdMembers;
    _syncedHouseholdMembers: HouseholdMembers;

    constructor(householdMembers: HouseholdMembers) {
        this._initialHouseholdMembers = householdMembers;
        this._syncedHouseholdMembers = householdMembers;
    }

    getInitialHouseholdMembers = (): HouseholdMembers => {
        return this._initialHouseholdMembers;
    };

    getSyncedMembers = (): HouseholdMembers => {
        return this._syncedHouseholdMembers;
    };

    restoreHouseholdMembers = (): void => {
        this._syncedHouseholdMembers = this._initialHouseholdMembers;
    };

    syncEscCommonData = (partToSync: PartToSyncEmployerCoverage, identifiers: Identifiers): void => {
        const { applicantGuid, companyName } = identifiers;

        this._syncedHouseholdMembers =
            this._initialHouseholdMembers
                .map((member: HouseholdMember) => {
                    const coverages: EmployerSponsoredCoverage[] = member.employerSponsoredCoverage;

                    const syncedCoverages = coverages.reduce((
                        coverages: EmployerSponsoredCoverage[],
                        coverage: EmployerSponsoredCoverage
                    ): EmployerSponsoredCoverage[] => {
                        const {
                            applicantGuid: coverageApplicantGuid,
                            companyName: coverageCompanyName
                        } = coverage;

                        const isTheSameCoverageEntity = coverageApplicantGuid === applicantGuid
                            && coverageCompanyName === companyName;

                        return isTheSameCoverageEntity
                            ? [
                                ...coverages,
                                {
                                    ...coverage,
                                    ...partToSync
                                }
                            ] :
                            [
                                ...coverages,
                                coverage
                            ];
                    }, []);

                    return {
                        ...member,
                        employerSponsoredCoverage: syncedCoverages
                    };
                });
    };

    syncHraCommonData = (partToSync: PartToSyncHra, identifiers: Identifiers): void => {
        const { applicantGuid, employerName } = identifiers;

        this._syncedHouseholdMembers =
            this._initialHouseholdMembers
                .map((member: HouseholdMember) => {
                    const hras: Array<HraDetails> = member.healthReimbursementArrangement;

                    const syncedHras = hras.reduce((
                        hras: Array<HraDetails>,
                        hra: HraDetails
                    ): Array<HraDetails> => {
                        const {
                            applicantGuid: hraApplicantGuid,
                            employerName: hraEmployerName
                        } = hra;

                        const isTheSameHraEntity = hraApplicantGuid === applicantGuid
                            && hraEmployerName === employerName;

                        return isTheSameHraEntity
                            ? [
                                ...hras,
                                {
                                    ...hra,
                                    ...partToSync
                                }
                            ] :
                            [
                                ...hras,
                                hra
                            ];
                    }, []);

                    return {
                        ...member,
                        healthReimbursementArrangement: syncedHras
                    };
                });
    };

    pullHraData = (paths: Array<string>, identifiers: Identifiers, hhmIndex: number, hraIndex: number) => {
        const { applicantGuid, employerName } = identifiers;

        const hras = this._initialHouseholdMembers
            .reduce(
                (hras: Array<HraDetails>, hhm: HouseholdMember): Array<HraDetails> => {
                    const currentHhmHras = hhm.healthReimbursementArrangement;
                    return [
                        ...hras,
                        ...currentHhmHras
                    ];
                },
                []
            );

        const foundHra: HraDetails | undefined = hras.find((hra: HraDetails): boolean => {
            const {
                applicantGuid: hraApplicantGuid,
                employerName: hraEmployerName,
                hraType
            } = hra;

            return hraApplicantGuid === applicantGuid && hraEmployerName === employerName && !!hraType;
        });

        if(!!foundHra) {
            const propsToSync = pick(foundHra, paths);
            this._syncedHouseholdMembers[hhmIndex].healthReimbursementArrangement =
                this._syncedHouseholdMembers[hhmIndex].healthReimbursementArrangement.reduce(
                    (hras: Array<HraDetails>, hra: HraDetails): Array<HraDetails> => {
                        const {
                            applicantGuid,
                            employerName,
                            hraType
                        } = hra;

                        const {
                            applicantGuid: applicantGuidFromFound,
                            employerName: employerNameFromFound
                        } = foundHra;

                        if(applicantGuid === applicantGuidFromFound && employerName === employerNameFromFound && !hraType) {
                            return [
                                ...hras,
                                {
                                    ...hra,
                                    ...propsToSync
                                }
                            ];
                        } else {
                            return [
                                ...hras,
                                hra
                            ];
                        }
                    },
                    [ ]
                );
        }
    };
}

export { HouseholdMembersSynchronizer };